import React, { useState } from 'react';
import { ButtonIcon, Modal, Textarea } from '../../../../components';
import { ModalFooter } from '../../../../components/modal/Modal';
import { CancelButtonModal, ConfirmButtonModal } from '../../../../global';
import { ADMIN_ROUTES, GLOBAL_ICONS, ROUTES } from '../../../../util/vars';
import { DateTime } from 'luxon';
import { getDatesParams } from '../../../../helper/getDatesParams';
import { repository } from '@whizz/api-client';
import { useDateRangeSelector } from '../../../../hooks/filters/useDateRangeSelector';
import { useImpactReportTemplateSelector } from '../../../../hooks/useImpactReportTemplateSelector';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';

interface Props {
  toggle: () => void;
  schoolIds: number[];
  onSuccess: () => void;
}

const serializeDate = (date: DateTime | string | null): string => {
  if (DateTime.isDateTime(date)) {
    return date.toISO() ?? new Date().toISOString();
  }

  return date ?? new Date().toISOString();
};

const BulkImpactReportModal = ({ toggle, schoolIds, onSuccess }: Props): JSX.Element => {
  const history = useHistory();
  const [ prompt, setPrompt ] = useState('');
  const [ dateRangeSelector, dateValue, customDateValue ] = useDateRangeSelector({ maxDate: new Date(), label: 'Generate using data from...', useCurrentAcademicYearOrLastAssessment: true });
  const selectedDateInterval = dateValue?.value ?? 'last_week';
  const {
    startDateParam,
    endDateParam,
  } = getDatesParams(selectedDateInterval, customDateValue, null, null);
  const serializedStartDate = serializeDate(startDateParam);
  const serializedEndDate = serializeDate(endDateParam);

  const bulkGenerateMutation = useMutation({
    mutationFn: (template: string) =>
      repository.SchoolImpactReportRepository.bulkGenerate({
        schoolIds,
        template,
        prompt,
        startDate: serializedStartDate,
        endDate: serializedEndDate,
      }),
    onSuccess: (data) => {
      onSuccess();
      toggle();
      history.push(`${ROUTES.superAdmin}${ADMIN_ROUTES.bulkImpactReports}/${data.bulkJobId}`);
    },
  });

  const [ templateSelector, template ] = useImpactReportTemplateSelector({ disabled: bulkGenerateMutation.isLoading });

  const renderHeader = () => (
    <div className={'container flex items-center pb-md border-b-2 border-header'}>
      <h5 className={'font-bold'}>
        Bulk Impact Reports
      </h5>
      <div className={'ml-auto'}>
        <ButtonIcon
          icon={GLOBAL_ICONS.close}
          onClick={() => toggle()}
          size={'sm'}
          color={'light'}
          isOutline
          isCircle
        />
      </div>
    </div>
  );

  return (
    <Modal
      open
      toggle={() => toggle()}
      onClose={() => toggle()}
      size={'lg'}
    >
      {renderHeader()}
      <div className={'container flex flex-col gap-5'}>
        <p>
          Generate and auto-send impact reports for {schoolIds.length} selected schools.
        </p>
        {templateSelector}
        <div>
          {dateRangeSelector}
        </div>
        <Textarea
          id={'bulk_impact_report_prompt'}
          label={'Prompt'}
          placeholder={'Optional prompt'}
          onChange={(event) => setPrompt(event.target.value)}
          value={prompt}
          disabled={bulkGenerateMutation.isLoading}
        />
        {bulkGenerateMutation.isError ? (
          <p className={'text-danger'}>
            {bulkGenerateMutation.error instanceof Error ? bulkGenerateMutation.error.message : 'Failed to start bulk generation.'}
          </p>
        ) : null}
      </div>
      <ModalFooter justify={'between'}>
        <CancelButtonModal
          disabled={bulkGenerateMutation.isLoading}
          onClick={() => toggle()}
        >
          Cancel
        </CancelButtonModal>
        <ConfirmButtonModal
          disabled={template === null || bulkGenerateMutation.isLoading}
          onClick={() => template && bulkGenerateMutation.mutate(template)}
        >
          Generate & Send
        </ConfirmButtonModal>
      </ModalFooter>
    </Modal>
  );
};

export { BulkImpactReportModal };
